var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, LitElement } from "lit";
import { property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import "../icon/icon.js";
import "../utils/expand-transition";
import { detectLocale } from "../i18n.js";
import "../utils/unstyled-heading";
import { reset } from "../styles.js";
import { styles } from "./styles.js";
/**
 * Expandable is a layout component used for creating expandable content areas on a page.
 *
 * [Warp component reference](https://warp-ds.github.io/docs/components/expandable/frameworks/elements)
 *
 * @slot title - Alternative to the `title` attribute should you need to provide some additional markup.
 *
 * @csspart base - the root wrapper element inside the component.
 * @csspart chevron - the chevron container.
 */
class WarpExpandable extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Controls component's expanded state
         */
        this.expanded = false;
        /**
         * Will make the expandable a Box
         */
        this.box = false;
        /**
         * Will make the expandable full-width on the sm breakpoint size
         */
        this.bleed = false;
        /**
         * Controls chevron visibility
         */
        this.noChevron = false;
        /**
         * Will animate the expansion/collapse
         */
        this.animated = false;
        this._hasTitle = true;
        this._showChevronUp = false;
    }
    // Slotted elements remain in lightDOM which allows for control of their style outside of shadowDOM.
    // ::slotted([Simple Selector]) confirms to Specificity rules, but (being simple) does not add weight to lightDOM skin selectors,
    // so never gets higher Specificity. Thus in order to overwrite style linked within shadowDOM, we need to use !important.
    // https://stackoverflow.com/a/61631668
    static { this.styles = [reset, styles]; }
    updated(changedProperties) {
        // We need a slight delay for the animation since it has a transition-duration of 150ms:
        if (changedProperties.has("expanded")) {
            setTimeout(() => {
                this._showChevronUp = this.expanded;
            }, 200);
        }
    }
    firstUpdated() {
        const hasTitleProp = Boolean(this.title);
        const hasTitleSlot = this.renderRoot.querySelector("slot[name='title']")?.assignedNodes().length > 0;
        this._hasTitle = hasTitleProp || hasTitleSlot;
    }
    get #chevronIcon() {
        const locale = detectLocale();
        return this._showChevronUp
            ? html `<w-icon
					class="chevron-icon"
					data-direction="up"
					name="ChevronUp"
					size="small"
					locale="${locale}"
				></w-icon>`
            : html `<w-icon
					class="chevron-icon"
					data-direction="down"
					name="ChevronDown"
					size="small"
					locale="${locale}"
				></w-icon>`;
    }
    /** @internal */
    get _expandableSlot() {
        const contentClass = this.contentClass
            ? `content ${this.contentClass}`
            : "content";
        return html `<div
			class=${contentClass}
			data-has-title=${ifDefined(this._hasTitle ? "" : undefined)}
		>
			<slot></slot>
		</div>`;
    }
    render() {
        const buttonClass = this.buttonClass
            ? `button ${this.buttonClass}`
            : "button";
        return html ` <div part="base">
			${this._hasTitle
            ? html `<w-unstyled-heading level=${ifDefined(this.headingLevel)}>
							<button
								class=${buttonClass}
								type="button"
								aria-expanded="${this.expanded}"
								@click=${() => (this.expanded = !this.expanded)}
							>
								<div class="title">
									${this.title
                ? html `<span class="title-text">${this.title}</span>`
                : html `<slot class="title-text" name="title"></slot>`}
									${this.noChevron
                ? ""
                : html `<div part="chevron">${this.#chevronIcon}</div>`}
								</div>
							</button>
						</w-unstyled-heading>`
            : ""}
			${this.animated
            ? html `<w-expand-transition class="expansion" ?show=${this.expanded}>
							${this._expandableSlot}
						</w-expand-transition>`
            : html `<div
							class="expansion"
							aria-hidden=${ifDefined(!this.expanded ? true : undefined)}
						>
							${this._expandableSlot}
						</div>`}
		</div>`;
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], WarpExpandable.prototype, "expanded", void 0);
__decorate([
    property({ type: String })
], WarpExpandable.prototype, "title", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpExpandable.prototype, "box", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], WarpExpandable.prototype, "bleed", void 0);
__decorate([
    property({ attribute: "button-class", type: String })
], WarpExpandable.prototype, "buttonClass", void 0);
__decorate([
    property({ attribute: "content-class", type: String })
], WarpExpandable.prototype, "contentClass", void 0);
__decorate([
    property({ attribute: "no-chevron", type: Boolean })
], WarpExpandable.prototype, "noChevron", void 0);
__decorate([
    property({ type: Boolean })
], WarpExpandable.prototype, "animated", void 0);
__decorate([
    property({ attribute: "heading-level", type: Number })
], WarpExpandable.prototype, "headingLevel", void 0);
__decorate([
    property({ type: Boolean, state: true })
], WarpExpandable.prototype, "_hasTitle", void 0);
__decorate([
    property({ type: Boolean, state: true })
], WarpExpandable.prototype, "_showChevronUp", void 0);
if (!customElements.get("w-expandable")) {
    customElements.define("w-expandable", WarpExpandable);
}
export { WarpExpandable };
//# sourceMappingURL=expandable.js.map